import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  FiUsers,
  FiGlobe,
  FiChevronDown,
  FiChevronLeft,
  FiX,
  FiRefreshCw,
  FiCheck,
  FiShare2,
  FiSearch,
  FiUser,
  FiAlertCircle,
} from 'react-icons/fi';
import {
  createCategoryShare,
  listCategoryShares,
  revokeCategoryShare,
  regenerateCategoryShare,
  refreshCategoryShare,
  categoryShareUrl,
  listAgencyUsers,
  shareCategoryWithUsers,
  listCategoryDirectShares,
  type CategoryShareScope,
  type AgencyUser,
  type DirectShareResult,
  type CategoryDirectShareRecord,
} from '../services/categoryShareApi';

interface CategoryShareMenuModalProps {
  categoryId: number;
  categoryName: string;
  onClose: () => void;
}

type View = 'menu' | 'users' | 'link';

// Command Center counterpart to AgentShareMenuModal, for a user-created SOP
// category. Two ways to share, picked from a menu: "Share with teammates"
// (pick people in your agency — each gets their own copy right away, the way
// Install for Agency fans an agent out) and "Share via Link" (anyone with the
// link adds their own copy). Both are served by
// routes/command_center_category_shares.py. Categories have no marketplace
// listing, so there is no Publish row. Portalled to body, like the config
// panel's own modals, so the sidebar's overflow/stacking never clips it.
const CategoryShareMenuModal: React.FC<CategoryShareMenuModalProps> = ({ categoryId, categoryName, onClose }) => {
  const [view, setView] = useState<View>('menu');
  const [error, setError] = useState('');

  // ── Share with teammates ──
  const [agencyUsers, setAgencyUsers] = useState<AgencyUser[] | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [sharing, setSharing] = useState(false);
  const [shareResults, setShareResults] = useState<DirectShareResult[] | null>(null);
  // Teammates who already received a copy — shown as a chip in the picker and
  // counted on the menu row. Best-effort: a failed load just hides the chips.
  const [recipients, setRecipients] = useState<CategoryDirectShareRecord[]>([]);

  // ── Share via link ──
  const [scope, setScope] = useState<CategoryShareScope>('agency');
  const [scopeMenuOpen, setScopeMenuOpen] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);
  const [linkResult, setLinkResult] = useState<{ id: number; token: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [refreshed, setRefreshed] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);

  // Guards async results after the modal closes. Deliberately NOT a per-effect
  // `cancelled` flag: an effect that also depends on the loading state it sets
  // re-runs on that very change, and its cleanup would discard the response
  // and leave the spinner on forever.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadRecipients = useCallback(() => {
    listCategoryDirectShares(categoryId)
      .then((list) => {
        if (mountedRef.current) setRecipients(list);
      })
      .catch(() => {});
  }, [categoryId]);

  // Teammates load once when the modal opens — the list is small and the
  // picker is one click away. Try again re-calls this after a failure.
  const loadUsers = useCallback(() => {
    setUsersLoading(true);
    listAgencyUsers()
      .then((users) => {
        if (mountedRef.current) setAgencyUsers(users);
      })
      .catch((e: any) => {
        if (mountedRef.current) setError(e.message || 'Failed to load your teammates');
      })
      .finally(() => {
        if (mountedRef.current) setUsersLoading(false);
      });
  }, []);

  // Load any link already active for this category, so re-opening Share shows
  // the existing one (copy / refresh / revoke) instead of only ever offering to
  // mint a brand-new link on top of it — same behavior as AgentShareMenuModal.
  useEffect(() => {
    let cancelled = false;
    listCategoryShares(categoryId)
      .then((shares) => {
        if (cancelled) return;
        const existing = shares[shares.length - 1];
        if (existing) {
          setLinkResult({ id: existing.id, token: existing.token });
          setScope(existing.scope);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingExisting(false);
      });
    loadRecipients();
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [categoryId, loadRecipients, loadUsers]);

  const alreadySharedIds = useMemo(() => new Set(recipients.map((r) => r.user_id)), [recipients]);

  const filteredUsers = useMemo(() => {
    const list = agencyUsers ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }, [agencyUsers, search]);

  const toggleUser = (id: number) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const goTo = (next: View) => {
    setError('');
    setView(next);
  };

  const handleShareWithUsers = async () => {
    if (selectedIds.length === 0) return;
    setSharing(true);
    setError('');
    try {
      const { results } = await shareCategoryWithUsers(categoryId, selectedIds);
      setShareResults(results);
      setSelectedIds([]);
      loadRecipients();
    } catch (e: any) {
      setError(e.message || 'Failed to share with your teammates');
    } finally {
      setSharing(false);
    }
  };

  const handleCreateLink = async () => {
    setCreatingLink(true);
    setError('');
    try {
      const share = await createCategoryShare(categoryId, scope);
      setLinkResult({ id: share.id, token: share.token });
    } catch (e: any) {
      setError(e.message || 'Failed to create share link');
    } finally {
      setCreatingLink(false);
    }
  };

  const handleCopy = (token: string) => {
    navigator.clipboard?.writeText(categoryShareUrl(token)).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevoke = async () => {
    if (!linkResult) return;
    setLinkBusy(true);
    setError('');
    try {
      await revokeCategoryShare(linkResult.id);
      setLinkResult(null);
    } catch (e: any) {
      setError(e.message || 'Failed to revoke share link');
    } finally {
      setLinkBusy(false);
    }
  };

  const handleRegenerate = async () => {
    if (!linkResult) return;
    setLinkBusy(true);
    setError('');
    try {
      const regenerated = await regenerateCategoryShare(linkResult.id);
      setLinkResult({ id: regenerated.id, token: regenerated.token });
    } catch (e: any) {
      setError(e.message || 'Failed to regenerate share link');
    } finally {
      setLinkBusy(false);
    }
  };

  const handleRefresh = async () => {
    if (!linkResult) return;
    setLinkBusy(true);
    setError('');
    try {
      await refreshCategoryShare(linkResult.id);
      setRefreshed(true);
      setTimeout(() => setRefreshed(false), 2500);
    } catch (e: any) {
      setError(e.message || 'Failed to refresh share link');
    } finally {
      setLinkBusy(false);
    }
  };

  const title =
    view === 'menu' ? `Share "${categoryName}"` : view === 'users' ? 'Share with teammates' : 'Share via Link';

  const created = (shareResults ?? []).filter((r) => r.status === 'created');
  const notCreated = (shareResults ?? []).filter((r) => r.status !== 'created' && r.status !== 'owner');

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(6px) saturate(1.2)',
        WebkitBackdropFilter: 'blur(6px) saturate(1.2)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role='dialog'
        aria-modal='true'
        style={{
          background: 'var(--surface-card, #1a1a1f)',
          backdropFilter: 'blur(24px) saturate(1.3)',
          WebkitBackdropFilter: 'blur(24px) saturate(1.3)',
          border: '1.5px solid var(--border-glass, rgba(255,255,255,0.1))',
          borderRadius: 18,
          padding: 24,
          width: '100%',
          maxWidth: 440,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          boxShadow: '0 32px 80px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {view !== 'menu' ? (
            <button
              type='button'
              onClick={() => goTo('menu')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                color: 'var(--text-secondary)',
                display: 'flex',
              }}
              aria-label='Back'
            >
              <FiChevronLeft size={18} />
            </button>
          ) : (
            <FiShare2 size={18} style={{ color: '#60a5fa', flexShrink: 0 }} />
          )}
          <h3
            style={{
              margin: 0,
              fontSize: 17,
              fontWeight: 700,
              color: 'var(--text-primary)',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={title}
          >
            {title}
          </h3>
          <button
            type='button'
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-tertiary)',
              display: 'flex',
              padding: 4,
            }}
            aria-label='Close'
          >
            <FiX size={18} />
          </button>
        </div>

        {error && (
          <p role='alert' style={{ color: '#f87171', fontSize: 12.5, margin: 0 }}>
            {error}
          </p>
        )}

        {view === 'menu' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button type='button' onClick={() => goTo('users')} style={menuRowStyle}>
              <FiUsers size={18} style={{ color: '#4ade80', flexShrink: 0 }} />
              <div style={{ textAlign: 'left' }}>
                <div style={menuRowTitleStyle}>Share with teammates</div>
                <div style={menuRowSubStyle}>
                  {recipients.length > 0
                    ? `Shared with ${recipients.length} teammate${recipients.length === 1 ? '' : 's'} — share with more`
                    : 'Pick people in your agency — each gets their own copy right away'}
                </div>
              </div>
            </button>
            <button type='button' onClick={() => goTo('link')} style={menuRowStyle}>
              <FiShare2 size={18} style={{ color: '#60a5fa', flexShrink: 0 }} />
              <div style={{ textAlign: 'left' }}>
                <div style={menuRowTitleStyle}>Share via Link</div>
                <div style={menuRowSubStyle}>
                  {linkResult ? 'A link is active — copy, refresh or revoke it' : 'Anyone with the link adds their own copy'}
                </div>
              </div>
            </button>
          </div>
        )}

        {view === 'users' && shareResults === null && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Each teammate gets their own independent copy in Wait for Human Approval mode, assigned to their
              agent with the same name as yours when they have one, otherwise to them. They can rename it or
              change its run mode afterwards.
            </p>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                borderRadius: 10,
                background: 'var(--surface-glass)',
                border: '1px solid var(--border-glass)',
              }}
            >
              <FiSearch size={14} aria-hidden='true' style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
              <input
                type='text'
                aria-label='Search teammates'
                placeholder='Search teammates…'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ flex: 1, background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: 13 }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', maxHeight: 300 }}>
              {usersLoading ? (
                <p style={{ fontSize: 12.5, color: 'var(--text-tertiary)', margin: '8px 0' }}>Loading teammates…</p>
              ) : agencyUsers === null ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0' }}>
                  <p style={{ fontSize: 12.5, color: 'var(--text-tertiary)', margin: 0, flex: 1 }}>
                    Couldn&apos;t load your teammates.
                  </p>
                  <button type='button' className='ca-fp-editor__btn ca-fp-editor__btn--ghost' onClick={loadUsers}>
                    Try again
                  </button>
                </div>
              ) : filteredUsers.length === 0 ? (
                <p style={{ fontSize: 12.5, color: 'var(--text-tertiary)', margin: '8px 0' }}>
                  {agencyUsers.length === 0 ? 'No other active users in your agency yet.' : 'No teammates match.'}
                </p>
              ) : (
                filteredUsers.map((u) => {
                  const isSelected = selectedIds.includes(u.id);
                  const already = alreadySharedIds.has(u.id);
                  return (
                    <button
                      key={u.id}
                      type='button'
                      className='mc-a11y-btn'
                      aria-pressed={isSelected}
                      onClick={() => toggleUser(u.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        background: isSelected ? 'var(--accent-purple-deep, #7c3aed)22' : 'var(--surface-glass)',
                        border: `1px solid ${isSelected ? 'var(--accent-purple-deep, #7c3aed)' : 'var(--border-glass)'}`,
                        borderRadius: 11,
                        cursor: 'pointer',
                        textAlign: 'left',
                        width: '100%',
                      }}
                    >
                      <FiUser size={16} aria-hidden='true' style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 13.5,
                            fontWeight: 650,
                            color: 'var(--text-primary)',
                          }}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
                          {already && <span style={chipStyle}>Shared</span>}
                        </div>
                        {u.email && (
                          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>{u.email}</div>
                        )}
                      </div>
                      {isSelected && (
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            flexShrink: 0,
                            background: 'var(--accent-purple-deep, #7c3aed)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                          }}
                        >
                          <FiCheck size={12} />
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <span aria-live='polite' style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                {selectedIds.length} selected
              </span>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type='button'
                  className='ca-fp-editor__btn ca-fp-editor__btn--ghost'
                  onClick={() => goTo('menu')}
                  disabled={sharing}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='ca-fp-editor__btn ca-fp-editor__btn--save'
                  onClick={handleShareWithUsers}
                  disabled={sharing || usersLoading || selectedIds.length === 0}
                >
                  {sharing ? (
                    <>
                      <FiRefreshCw size={12} className='ca-fp-spin' /> Sharing…
                    </>
                  ) : (
                    'Share'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {view === 'users' && shareResults !== null && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {created.length > 0 ? (
                <FiCheck size={18} style={{ color: '#4ade80', flexShrink: 0 }} />
              ) : (
                <FiAlertCircle size={18} style={{ color: '#fbbf24', flexShrink: 0 }} />
              )}
              <div style={{ fontSize: 13.5, fontWeight: 650, color: 'var(--text-primary)' }}>
                {created.length > 0
                  ? `Shared with ${created.length} teammate${created.length === 1 ? '' : 's'}`
                  : 'Nothing new to share'}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', maxHeight: 300 }}>
              {created.map((r) => (
                <div key={r.user_id} style={resultRowStyle}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{r.name}</div>
                  <div style={menuRowSubStyle}>
                    {r.assigned_to && r.assigned_to !== 'them' ? `Copy added, assigned to ${r.assigned_to}` : 'Copy added, assigned to them'}
                  </div>
                </div>
              ))}
              {notCreated.map((r) => (
                <div key={r.user_id} style={{ ...resultRowStyle, borderColor: r.status === 'failed' ? 'rgba(248,113,113,0.5)' : 'var(--border-glass)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{r.name}</div>
                  <div style={{ ...menuRowSubStyle, color: r.status === 'failed' ? '#f87171' : 'var(--text-tertiary)' }}>
                    {r.reason || (r.status === 'already_has' ? 'Already has a category with this name' : 'Could not be shared')}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type='button'
                className='ca-fp-editor__btn ca-fp-editor__btn--ghost'
                onClick={() => {
                  setShareResults(null);
                  setSearch('');
                }}
              >
                Share with more
              </button>
              <button type='button' className='ca-fp-editor__btn ca-fp-editor__btn--save' onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        )}

        {view === 'link' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Anyone who opens the link adds their own independent copy of this category to their Command
              Center — the trigger, labels and prompts, never a reference to yours. Run mode and assignments
              stay theirs to set: a category assigned to one of your Digital Workforce agents is offered to
              one of their agents with the same name. Deleting this category turns its links off.
            </p>

            {loadingExisting && (
              <p style={{ color: 'var(--text-tertiary)', fontSize: 12.5, margin: 0 }}>Checking for an existing link…</p>
            )}

            {!loadingExisting && !linkResult && (
              <>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>
                    General access
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={avatarStyle}>{scope === 'anyone' ? <FiGlobe size={17} /> : <FiUsers size={17} />}</div>
                    <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                      <button type='button' onClick={() => setScopeMenuOpen((o) => !o)} style={dropdownBtnStyle}>
                        <span style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-primary)' }}>
                          {scope === 'anyone' ? 'Anyone with the link' : 'Anyone in this agency'}
                        </span>
                        <FiChevronDown size={15} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                      </button>
                      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 4 }}>
                        {scope === 'anyone'
                          ? 'With this link, even people outside your agency add their own copy'
                          : 'Anyone in your agency with this link can add their own copy'}
                      </div>

                      {scopeMenuOpen && (
                        <div style={dropdownMenuStyle}>
                          <button
                            type='button'
                            onClick={() => {
                              setScope('agency');
                              setScopeMenuOpen(false);
                            }}
                            style={dropdownItemStyle}
                          >
                            <FiUsers size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                            <div style={{ textAlign: 'left', flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-primary)' }}>
                                Anyone in this agency
                              </div>
                              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                                Only people in your agency with the link
                              </div>
                            </div>
                            {scope === 'agency' && (
                              <FiCheck size={15} style={{ color: 'var(--accent-purple-deep)', flexShrink: 0 }} />
                            )}
                          </button>
                          <button
                            type='button'
                            onClick={() => {
                              setScope('anyone');
                              setScopeMenuOpen(false);
                            }}
                            style={{ ...dropdownItemStyle, borderBottom: 'none' }}
                          >
                            <FiGlobe size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                            <div style={{ textAlign: 'left', flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-primary)' }}>
                                Anyone with the link
                              </div>
                              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                                Even people outside your agency, with the link
                              </div>
                            </div>
                            {scope === 'anyone' && (
                              <FiCheck size={15} style={{ color: 'var(--accent-purple-deep)', flexShrink: 0 }} />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button
                    type='button'
                    className='ca-fp-editor__btn ca-fp-editor__btn--ghost'
                    onClick={() => goTo('menu')}
                    disabled={creatingLink}
                  >
                    Cancel
                  </button>
                  <button
                    type='button'
                    className='ca-fp-editor__btn ca-fp-editor__btn--save'
                    onClick={handleCreateLink}
                    disabled={creatingLink}
                  >
                    {creatingLink ? (
                      <>
                        <FiRefreshCw size={12} className='ca-fp-spin' /> Creating…
                      </>
                    ) : (
                      'Create link'
                    )}
                  </button>
                </div>
              </>
            )}

            {linkResult && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={avatarStyle}>{scope === 'anyone' ? <FiGlobe size={17} /> : <FiUsers size={17} />}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-primary)' }}>
                      {scope === 'anyone' ? 'Anyone with the link' : 'Anyone in this agency'}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>
                      can add their own copy
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    readOnly
                    value={categoryShareUrl(linkResult.token)}
                    className='ca-fp-editor__textarea'
                    style={{ minHeight: 'auto', height: 38, padding: '8px 12px', fontSize: 12, flex: 1 }}
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <button
                    type='button'
                    className='ca-fp-editor__btn ca-fp-editor__btn--save'
                    onClick={() => handleCopy(linkResult.token)}
                  >
                    {copied ? 'Copied!' : 'Copy link'}
                  </button>
                </div>
                <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                  Edited the category since sharing this? <strong>Refresh</strong> updates the link&apos;s content in
                  place — same URL, nothing breaks for people who already have it.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button
                    type='button'
                    className='ca-fp-editor__btn ca-fp-editor__btn--ghost'
                    disabled={linkBusy}
                    onClick={handleRefresh}
                  >
                    {linkBusy ? 'Working…' : refreshed ? 'Refreshed!' : 'Refresh'}
                  </button>
                  <button
                    type='button'
                    className='ca-fp-editor__btn ca-fp-editor__btn--ghost'
                    disabled={linkBusy}
                    onClick={handleRegenerate}
                  >
                    {linkBusy ? 'Working…' : 'Regenerate'}
                  </button>
                  <button
                    type='button'
                    className='ca-fp-editor__btn ca-fp-editor__btn--danger-ghost'
                    disabled={linkBusy}
                    onClick={handleRevoke}
                  >
                    {linkBusy ? 'Working…' : 'Revoke'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

const menuRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '12px 14px',
  background: 'var(--surface-glass)',
  border: '1px solid var(--border-glass)',
  borderRadius: 11,
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
};
const menuRowTitleStyle: React.CSSProperties = { fontSize: 13.5, fontWeight: 650, color: 'var(--text-primary)' };
const menuRowSubStyle: React.CSSProperties = { fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 };
const resultRowStyle: React.CSSProperties = {
  padding: '9px 12px',
  background: 'var(--surface-glass)',
  border: '1px solid var(--border-glass)',
  borderRadius: 10,
};
const chipStyle: React.CSSProperties = {
  fontSize: 9.5,
  fontWeight: 700,
  color: '#4ade80',
  background: 'rgba(74,222,128,0.12)',
  border: '1px solid rgba(74,222,128,0.3)',
  borderRadius: 999,
  padding: '1px 7px',
  flexShrink: 0,
};
const avatarStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  flexShrink: 0,
  background: 'var(--surface-glass)',
  border: '1px solid var(--border-glass)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-secondary)',
};
const dropdownBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  width: '100%',
  padding: '8px 12px',
  borderRadius: 8,
  cursor: 'pointer',
  textAlign: 'left',
  background: 'var(--surface-glass)',
  border: '1px solid var(--border-glass)',
};
const dropdownMenuStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  zIndex: 100,
  background: 'var(--surface-card, #1a1a1f)',
  border: '1px solid var(--border-glass)',
  borderRadius: 10,
  boxShadow: '0 16px 40px rgba(0,0,0,0.25)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};
const dropdownItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  width: '100%',
  background: 'none',
  border: 'none',
  borderBottom: '1px solid var(--border-glass)',
  cursor: 'pointer',
  textAlign: 'left',
};

export default CategoryShareMenuModal;
